// Meta-progression: everything that can be unlocked between runs (cores shop in
// the hangar + milestone rewards), reactor-colour cosmetics and challenge
// options. Phaser-free so it can be unit-tested and imported by GameScene.

import { SECTORS_PER_RUN } from '../config/constants';
import { COLORS } from '../config/palette';
import type { RunState } from '../core/run';
import { save } from '../core/save';
import type { BossId, MechId, UnlockId } from '../core/types';
import { SECTORS } from './sectors';

export type UnlockCategory = 'mech' | 'weapon' | 'equipment' | 'mutation' | 'cosmetic' | 'challenge';

export interface UnlockDef {
  id: UnlockId;
  name: string;
  desc: string;
  category: UnlockCategory;
  /** Price in cores (hangar shop). */
  cost: number;
  /** Mech the unlock belongs to (cosmetics). */
  mech?: MechId;
}

// ───────────────────────────── Cosmetics ─────────────────────────────

export interface CosmeticDef {
  /** 'cosmetic:<mech>:<variant>' ('…:default' is always owned). */
  id: string;
  mech: MechId;
  variant: string;
  name: string;
  /** Reactor colour applied with MechView.setReactorColor. */
  color: number;
  /** Lighter companion colour for UI glows. */
  glow: number;
  cost: number;
  /** Unlock id; undefined for the default finish. */
  unlock?: UnlockId;
}

function cosmetic(mech: MechId, variant: string, name: string, color: number, glow: number, cost = 30): CosmeticDef {
  const id = `cosmetic:${mech}:${variant}`;
  return { id, mech, variant, name, color, glow, cost, unlock: cost > 0 ? id : undefined };
}

/** Reactor colour variants per mech. The first entry is the factory finish. */
export const COSMETICS: Readonly<Record<MechId, readonly CosmeticDef[]>> = {
  volt: [
    cosmetic('volt', 'default', 'Factory Cyan', COLORS.volt, COLORS.voltGlow, 0),
    cosmetic('volt', 'crimson', 'Crimson Arc', 0xff4d6d, 0xffa3b4),
    cosmetic('volt', 'toxic', 'Toxic Surge', 0x9dff3a, 0xd2ffa0),
    cosmetic('volt', 'solar', 'Solar Flare', 0xffc93d, 0xffe49e),
  ],
  bastion: [
    cosmetic('bastion', 'default', 'Factory Amber', COLORS.bastion, COLORS.bastionGlow, 0),
    cosmetic('bastion', 'cobalt', 'Cobalt Bulwark', 0x4d8dff, 0xa6c6ff),
    cosmetic('bastion', 'verdant', 'Verdant Shell', 0x4dffa0, 0xa8ffd0),
    cosmetic('bastion', 'warhead', 'Warhead Red', 0xff4f3d, 0xffa196),
  ],
  wraith: [
    cosmetic('wraith', 'default', 'Factory Violet', COLORS.wraith, COLORS.wraithGlow, 0),
    cosmetic('wraith', 'spectral', 'Spectral Teal', 0x5dffe0, 0xb0fff0),
    cosmetic('wraith', 'bloodmoon', 'Blood Moon', 0xff3d6e, 0xff9fb6),
    cosmetic('wraith', 'pale', 'Pale Ghost', 0xe8ecff, 0xffffff),
  ],
};

/** The cosmetic currently selected for a mech (falls back to the factory finish if the saved one is not owned). */
export function selectedCosmetic(mechId: MechId): CosmeticDef {
  const list = COSMETICS[mechId];
  const chosen = save.data.cosmetics?.[mechId];
  const found = list.find((c) => c.id === chosen);
  if (found && save.isUnlocked(found.unlock)) return found;
  return list[0];
}

/** Reactor colour to apply to a mech view (selected cosmetic). */
export function reactorColorFor(mechId: MechId): number {
  return selectedCosmetic(mechId).color;
}

// ───────────────────────────── Challenges ─────────────────────────────

export interface ChallengeDef {
  /** Id stored in save.data.challenges and checked with run.hasChallenge(id). */
  id: string;
  unlock: UnlockId;
  name: string;
  desc: string;
}

export const CHALLENGES: readonly ChallengeDef[] = [
  {
    id: 'glass_frame',
    unlock: 'challenge:glass_frame',
    name: 'Glass Frame',
    desc: 'Your chassis runs stripped down: maximum HP is reduced by 40%.',
  },
  {
    id: 'no_repairs',
    unlock: 'challenge:no_repairs',
    name: 'No Repairs',
    desc: 'Repair bays pay out scrap instead of fixing you, and station repairs are offline.',
  },
  {
    id: 'elite_surge',
    unlock: 'challenge:elite_surge',
    name: 'Elite Surge',
    desc: 'Every combat wave is led by an elite enemy.',
  },
  {
    id: 'scarcity',
    unlock: 'challenge:scarcity',
    name: 'Scarcity',
    desc: 'Enemies never drop health, and loot rooms hold a single orb.',
  },
];

/** Core bonus per enabled challenge (applied by GameScene.computeCores). */
export const CHALLENGE_CORE_BONUS = 0.2;

// ───────────────────────────── Unlock catalogue ─────────────────────────────

export const UNLOCKS: readonly UnlockDef[] = [
  {
    id: 'mech:bastion',
    name: 'BASTION',
    desc: 'Heavy siege frame. Deploys a barrier that blocks fire and amplifies your shots.',
    category: 'mech',
    cost: 120,
  },
  {
    id: 'mech:wraith',
    name: 'WRAITH',
    desc: 'Aerial assassin frame. Phases through danger and leaves echoes that repeat your attacks.',
    category: 'mech',
    cost: 180,
  },
  {
    id: 'weapon:gravity_launcher',
    name: 'Gravity Launcher',
    desc: 'Lobbed grenade that opens a gravity well, drags enemies together, then implodes.',
    category: 'weapon',
    cost: 60,
  },
  {
    id: 'equip:jump_jets',
    name: 'Jump Jets',
    desc: 'Back-mounted thruster pack for extra air mobility. Adds to the dash tag.',
    category: 'equipment',
    cost: 40,
  },
  {
    id: 'equip:anchor_greaves',
    name: 'Anchor Greaves',
    desc: 'Heavy leg plating that roots the frame against knockback. Shield and kinetic tags.',
    category: 'equipment',
    cost: 40,
  },
  {
    id: 'mutation:meltdown_engine',
    name: 'Meltdown Engine',
    desc: 'Overheating no longer locks your weapon. Overheated shots hit 50% harder but cost 2 HP each.',
    category: 'mutation',
    cost: 50,
  },
  {
    id: 'mutation:berserk_servos',
    name: 'Berserk Servos',
    desc: 'Below 40% HP your servos redline: +35% damage and fire rate.',
    category: 'mutation',
    cost: 50,
  },
  ...(['volt', 'bastion', 'wraith'] as const).flatMap((mech) =>
    COSMETICS[mech]
      .filter((c) => c.unlock)
      .map(
        (c): UnlockDef => ({
          id: c.id,
          name: c.name,
          desc: `Reactor finish for ${mech.toUpperCase()}.`,
          category: 'cosmetic',
          cost: c.cost,
          mech,
        }),
      ),
  ),
  ...CHALLENGES.map(
    (c): UnlockDef => ({
      id: c.unlock,
      name: c.name,
      desc: `${c.desc} +${Math.round(CHALLENGE_CORE_BONUS * 100)}% cores while enabled.`,
      category: 'challenge',
      cost: 25,
    }),
  ),
];

export function unlockDef(id: string): UnlockDef | undefined {
  return UNLOCKS.find((u) => u.id === id);
}

/** Milestones that grant unlocks for free (shown as requirements in the hangar). */
export const MILESTONE_TEXT: Readonly<Record<string, string>> = {
  'mech:bastion': 'Defeat the Sector 1 boss',
  'mech:wraith': 'Defeat the Sector 2 boss',
  'challenge:glass_frame': 'Win a run',
  'challenge:no_repairs': 'Win a run',
  'challenge:elite_surge': 'Win a run',
  'challenge:scarcity': 'Win a run',
};

/** Purchase an unlock with cores. Returns false if already owned or unaffordable. */
export function buyUnlock(id: string): boolean {
  const def = unlockDef(id);
  if (!def || save.isUnlocked(id)) return false;
  if (!save.spendCores(def.cost)) return false;
  save.unlock(id);
  return true;
}

/** Boss order used when sector data is unavailable. */
const BOSS_ORDER: readonly BossId[] = ['warden', 'foundry_titan', 'singularity_heart'];

/**
 * Grants milestone unlocks earned by this run (via save.unlock) and returns their ids.
 * - first defeat of the sector-1 boss → mech:bastion
 * - first defeat of the sector-2 boss → mech:wraith
 * - first victory → every challenge option
 * Also records per-boss kill counts in the save.
 */
export function evaluateMilestones(run: RunState, victory: boolean): string[] {
  // Sector i's boss must fall before the run can enter sector i + 1, so the
  // sector index is a lower bound on the bosses defeated; bossKills covers the
  // boss of the sector the run ended in.
  const bossesDown = victory
    ? SECTORS_PER_RUN
    : Math.min(SECTORS_PER_RUN, Math.max(run.sectorIndex, run.stats.bossKills));

  const kills = save.data.bossKills ?? (save.data.bossKills = {});
  for (let i = 0; i < bossesDown; i++) {
    const boss = SECTORS[i]?.boss ?? BOSS_ORDER[i];
    kills[boss] = (kills[boss] ?? 0) + 1;
  }

  const earned: string[] = [];
  const grant = (id: string): void => {
    if (save.unlock(id)) earned.push(id);
  };
  if (bossesDown >= 1) grant('mech:bastion');
  if (bossesDown >= 2) grant('mech:wraith');
  if (victory) for (const c of CHALLENGES) grant(c.unlock);

  save.persist();
  return earned;
}
